import * as React from 'react'

import {
  ColumnDef,
  SortingState,
  VisibilityState,
  RowSelectionState,
  OnChangeFn,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  useReactTable,
  FilterFnOption,
  getFilteredRowModel,
  CellContext,
  HeaderContext,
} from '@tanstack/react-table'

import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationEllipsis,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination'

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useTranslation } from 'react-i18next'
import { Input } from './input'
import { useEffect } from 'react'
import { PulseLoader } from 'react-spinners'
import { TFunction } from 'i18next'
import { getErrorDetails } from 'app/frontend/base/helpers'
import { Checkbox } from './checkbox'
import { useTableSearchParams } from 'tanstack-table-search-params'
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import Card from 'app/frontend/base/components/Card'

// Imported from https://gist.github.com/nicostombros/fc9a16a67fc47a536cf84736d0b8cfd8

type PaginatorProps = {
  currentPage: number
  totalPages: number
  onPageChange: (pageNumber: number) => void
  showPreviousNext: boolean
  disabled?: boolean
}

const generatePaginationLinks = (
  currentPage: number,
  totalPages: number,
  onPageChange: (page: number) => void,
  disabled = false,
) => {
  const pages: JSX.Element[] = []
  if (totalPages <= 6) {
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <PaginationItem key={i}>
          <PaginationLink
            className='h-8 w-8'
            onClick={() => {
              if (disabled) return
              onPageChange(i)
            }}
            isActive={i === currentPage}
            disabled={disabled}
          >
            {i}
          </PaginationLink>
        </PaginationItem>,
      )
    }
  } else {
    for (let i = 1; i <= 2; i++) {
      pages.push(
        <PaginationItem key={`left-${i}`}>
          <PaginationLink
            className='h-8 w-8'
            onClick={() => {
              if (disabled) return
              onPageChange(i)
            }}
            isActive={i === currentPage}
            disabled={disabled}
          >
            {i}
          </PaginationLink>
        </PaginationItem>,
      )
    }
    if (2 < currentPage && currentPage < totalPages - 1) {
      pages.push(<PaginationEllipsis key='first-ellipsis' />)
      pages.push(
        <PaginationItem key={`middle-${currentPage}`}>
          <PaginationLink
            className='h-8 w-8'
            onClick={() => {
              if (disabled) return
              onPageChange(currentPage)
            }}
            isActive={true}
            disabled={disabled}
          >
            {currentPage}
          </PaginationLink>
        </PaginationItem>,
      )
    }
    pages.push(<PaginationEllipsis key='second-ellipsis' />)
    for (let i = totalPages - 1; i <= totalPages; i++) {
      pages.push(
        <PaginationItem key={`right-${i}`}>
          <PaginationLink
            className='h-8 w-8'
            onClick={() => {
              if (disabled) return
              onPageChange(i)
            }}
            isActive={i === currentPage}
            disabled={disabled}
          >
            {i}
          </PaginationLink>
        </PaginationItem>,
      )
    }
  }
  return pages
}

function TablePagination({
  currentPage,
  totalPages,
  onPageChange,
  showPreviousNext,
  disabled = false,
}: PaginatorProps) {
  if (totalPages === 1) {
    return null
  } else {
    return (
      <Pagination className='w-fit mx-0'>
        <PaginationContent className='flex-wrap justify-end'>
          {showPreviousNext && totalPages ? (
            <PaginationItem>
              <PaginationPrevious
                onClick={() => {
                  if (disabled) return
                  onPageChange(currentPage - 1)
                }}
                disabled={disabled || currentPage - 1 < 1}
              />
            </PaginationItem>
          ) : null}
          {generatePaginationLinks(
            currentPage,
            totalPages,
            onPageChange,
            disabled,
          )}
          {showPreviousNext && totalPages ? (
            <PaginationItem>
              <PaginationNext
                onClick={() => {
                  if (disabled) return
                  onPageChange(currentPage + 1)
                }}
                disabled={disabled || currentPage > totalPages - 1}
              />
            </PaginationItem>
          ) : null}
        </PaginationContent>
      </Pagination>
    )
  }
}

export interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  className?: string
  rowClassName?: (row: TData) => string
  headerClassName?: (index: number) => string
  cellClassName?: string
  data: TData[]
  emptyMessage?: () => string | React.ReactNode
  visibilityState?: VisibilityState
  sortingState?: SortingState
  onVisibleRowsChange?: (newRows: TData[]) => void
  onRowClick?: (row: TData) => void
  pagination?: number
  filterType?: FilterFnOption<TData>
  includeHeader: boolean
  customHeaderActions?: React.ReactNode
  getRowId?: (row: TData) => string
  rowSelection?: RowSelectionState
  onRowSelectionChange?: OnChangeFn<RowSelectionState>
  selectionFilterFn?: (row: TData) => boolean
  stickyFirstColumn?: boolean
  spanningRowMessage?: (row: TData) => string | React.ReactNode | null
  spanningRowMessageStartColumnIndex?: number
  wrapperComponent?: React.ComponentType<React.HTMLAttributes<HTMLDivElement>>
}

function DataTable<TData, TValue>({
  columns,
  className,
  rowClassName,
  headerClassName,
  cellClassName,
  data,
  visibilityState,
  sortingState,
  emptyMessage,
  pagination,
  onVisibleRowsChange,
  customHeaderActions,
  onRowClick,
  filterType,
  includeHeader = true,
  getRowId,
  rowSelection,
  onRowSelectionChange,
  selectionFilterFn,
  stickyFirstColumn,
  spanningRowMessage,
  spanningRowMessageStartColumnIndex = 0,
  wrapperComponent: WrapperComponent = Card,
}: DataTableProps<TData, TValue>) {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { pathname } = useLocation()

  const urlSyncConfig = useTableSearchParams(
    {
      query: searchParams,
      pathname,
      replace: (url) => navigate(url, { replace: true }),
    },
    {
      defaultValues: {
        pagination: {
          pageIndex: 0,
          pageSize: pagination || 10,
        },
        sorting: sortingState || [],
      },
    },
  )

  const table = useReactTable({
    ...(pagination || filterType ? urlSyncConfig : {}),
    data,
    columns: [
      ...(rowSelection
        ? [
            {
              id: 'selection',
              header: ({ table }: HeaderContext<TData, TValue>) => (
                <div
                  className='flex items-center cursor-pointer'
                  onClick={(event) => {
                    event.stopPropagation()
                    table.toggleAllPageRowsSelected()
                  }}
                >
                  <Checkbox
                    className='border-accent bg-card-nested'
                    checked={table.getIsAllPageRowsSelected()}
                    onClick={(event) => event.stopPropagation()}
                    onCheckedChange={() => table.toggleAllPageRowsSelected()}
                  />
                </div>
              ),
              cell: ({ row }: CellContext<TData, TValue>) =>
                row.getCanSelect() ? (
                  <div
                    className='flex items-center cursor-pointer'
                    onClick={(event) => {
                      event.stopPropagation()
                      row.toggleSelected()
                    }}
                  >
                    <Checkbox
                      className='border-accent bg-card-nested'
                      checked={row.getIsSelected()}
                      onClick={(event) => event.stopPropagation()}
                      onCheckedChange={() => row.toggleSelected()}
                    />
                  </div>
                ) : null,
            },
          ]
        : []),
      ...columns,
    ],
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: pagination ? getPaginationRowModel() : undefined,
    defaultColumn: {
      size: undefined,
    },
    initialState: {
      columnVisibility: visibilityState || {},
      pagination: pagination ? { pageSize: pagination || 10 } : undefined,
    },
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: filterType || 'auto',
    onSortingChange: urlSyncConfig.onSortingChange,
    getSortedRowModel: getSortedRowModel(),
    enableSortingRemoval: false,
    enableRowSelection: rowSelection
      ? (row) => (selectionFilterFn ? selectionFilterFn(row.original) : true)
      : false,
    onRowSelectionChange: onRowSelectionChange,
    state: {
      ...(pagination || filterType ? urlSyncConfig.state : {}),
      sorting: urlSyncConfig.state.sorting,
      ...(!filterType ? { globalFilter: '' } : {}),
      ...(rowSelection ? { rowSelection } : {}),
      ...(visibilityState ? { columnVisibility: visibilityState } : {}),
    },
    getRowId: getRowId,
  })

  useEffect(() => {
    const rows = table.getRowModel().rows
    onVisibleRowsChange?.(rows.map((row) => row.original))
  }, [
    table.getState().pagination,
    table.getState().globalFilter,
    table.getState().sorting,
    data.length,
  ])

  return (
    <WrapperComponent className={cn('p-0', className)}>
      {filterType ||
      (pagination && data.length > pagination && pagination > 25) ? (
        <div className='flex items-start justify-between m-3 gap-3 flex-wrap'>
          {filterType ? (
            <Input
              className='w-full max-w-60'
              value={(table.getState().globalFilter as string) ?? ''}
              onChange={(e) => table.setGlobalFilter(String(e.target.value))}
              placeholder='Search...'
            />
          ) : null}

          {customHeaderActions ? (
            customHeaderActions
          ) : pagination ? (
            <TablePagination
              currentPage={table.getState().pagination.pageIndex + 1}
              totalPages={table.getPageCount()}
              onPageChange={(page) => table.setPageIndex(page - 1)}
              showPreviousNext={true}
            />
          ) : null}
        </div>
      ) : null}

      <Table>
        {includeHeader && (
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup, groupIndex, groups) => (
              <TableRow
                key={headerGroup.id}
                className={
                  groupIndex < groups.length - 1 ? 'border-b-0' : undefined
                }
              >
                {headerGroup.headers.map((header, i) => {
                  const meta = header.column.columnDef.meta as
                    | { className?: string }
                    | undefined
                  return (
                    <TableHead
                      className={cn(
                        headerClassName?.(i),
                        meta?.className,
                        rowSelection &&
                          i === 0 &&
                          '[&:has([role=checkbox])]:pr-4',
                        rowSelection && i === 1 && 'pl-0',
                        stickyFirstColumn &&
                          i === 0 &&
                          'sticky left-0 z-1 bg-card shadow-[inset_-1px_0_0_var(--color-border)]',
                        stickyFirstColumn &&
                          groupIndex === 0 &&
                          'rounded-tl-md',
                      )}
                      key={header.id}
                      colSpan={header.colSpan}
                      style={{
                        minWidth: header.column.columnDef.size,
                        maxWidth: header.column.columnDef.size,
                        width: header.column.columnDef.size,
                      }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
        )}
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => {
              const rowMessage = spanningRowMessage?.(row.original)
              const visibleCells = row.getVisibleCells()
              const startColumnIndex = Math.max(
                0,
                Math.min(
                  spanningRowMessageStartColumnIndex,
                  Math.max(visibleCells.length - 1, 0),
                ),
              )
              const renderDataCell = (
                cell: typeof visibleCells[number],
                cellIndex: number,
              ) => {
                const meta = cell.column.columnDef.meta as
                  | { className?: string }
                  | undefined
                const isSelectionCell = cell.column.id === 'selection'
                return (
                  <TableCell
                    className={cn(
                      cellClassName,
                      meta?.className,
                      isSelectionCell && 'cursor-pointer',
                      isSelectionCell && '[&:has([role=checkbox])]:pr-4',
                      rowSelection && cellIndex === 1 && 'pl-0',
                      stickyFirstColumn &&
                        cellIndex === 0 &&
                        `sticky left-0 z-1 ${
                          rowMessage ? 'bg-warning' : 'bg-card'
                        } shadow-[inset_-1px_0_0_var(--color-border)]`,
                    )}
                    key={cell.id}
                    onClick={
                      isSelectionCell
                        ? (event) => {
                            event.stopPropagation()
                            cell.row.toggleSelected()
                          }
                        : undefined
                    }
                    style={{
                      minWidth: cell.column.columnDef.size,
                      maxWidth: cell.column.columnDef.size,
                      width: cell.column.columnDef.size,
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                )
              }
              return (
                <TableRow
                  key={row.id}
                  className={cn(
                    onRowClick ? 'cursor-pointer' : '',
                    rowMessage ? 'bg-warning text-warning-foreground' : '',
                    rowClassName?.(row.original),
                  )}
                  data-state={row.getIsSelected() && 'selected'}
                  onClick={() => (onRowClick ? onRowClick(row.original) : null)}
                >
                  {rowMessage ? (
                    <>
                      {visibleCells
                        .slice(0, startColumnIndex)
                        .map(renderDataCell)}
                      <TableCell
                        colSpan={Math.max(
                          visibleCells.length - startColumnIndex,
                          1,
                        )}
                        className='text-left'
                      >
                        {rowMessage}
                      </TableCell>
                    </>
                  ) : (
                    visibleCells.map(renderDataCell)
                  )}
                </TableRow>
              )
            })
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className='h-24 text-center'>
                {emptyMessage ? emptyMessage() : t('shared.no-results')}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {pagination && data.length > pagination ? (
        <div className='flex justify-end m-3'>
          <TablePagination
            currentPage={table.getState().pagination.pageIndex + 1}
            totalPages={table.getPageCount()}
            onPageChange={(page) => table.setPageIndex(page - 1)}
            showPreviousNext={true}
          />
        </div>
      ) : null}
    </WrapperComponent>
  )
}

const EmptyMessageWithLoader = ({
  emptyMessageKey,
  isPending,
  isLoadingError,
  error,
  t,
}: {
  emptyMessageKey: string
  isPending: boolean
  isLoadingError: boolean
  error: Error | null
  t: TFunction
}) => {
  if (isPending)
    return (
      <div className='my-8'>
        <PulseLoader
          loading={true}
          speedMultiplier={0.65}
          size={15}
          color='var(--color-primary)'
        />
      </div>
    )
  if (isLoadingError && error) {
    const { title, description } = getErrorDetails(error, t)
    return (
      <div className='my-8'>
        <h3>{title}</h3>
        <p>{description}</p>
      </div>
    )
  } else {
    return <div className='my-8'>{t(emptyMessageKey)}</div>
  }
}

export { DataTable, EmptyMessageWithLoader, TablePagination }
